import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@app/services/error.service';
import { FilterParams } from '@claranet/node-search-filter';
import { Embedded } from '@data/models/embed.model';
import { InventorySchema } from '@data/models/inventory-schema.model';
import {
    configuration as inventoryConfiguration,
    embed as inventoryEmbed,
    EmbedInventories,
    EmbedInventory,
    Inventory,
    LinkedInventories,
    Operation,
    BulkEditInventoryResponse,
} from '@data/models/inventory.model';

import {
    configuration as patchConfiguration,
    embed as patchEmbed,
    Patch,
    EmbedPatches,
} from '@data/models/patch.model';

import { ListMetadataKeys } from '@data/models/metadata.model';
import { CveRaw, Software, ToolAndService } from '@data/models/server.model';
import { toParams } from '@shared/utils/query-to-params';
import * as _ from 'lodash';
import { Observable } from 'rxjs';
import {
    catchError,
    map,
} from 'rxjs/operators';
import { MetadataService } from './metadata.service';
import { Tag } from '@data/models/tag.model';
import { isEmpty, uniq } from 'lodash';
import { Stat } from '@data/models/stat';

export const INVENTORY_SERVICE_API_ROOT = '/asset/v1/inventory';

@Injectable()
export class InventoryService {

    private errors;

    constructor(
        private errorService: ErrorService,
        private http: HttpClient,
        private metadataService: MetadataService,
    ) {
        this.errors = this.errorService.inventoryServiceErrors;
    }

    public findInventory<T extends inventoryEmbed>(filterParams: FilterParams, embedFields: T[] = []): Observable<EmbedInventories<T>> {
        const params = toParams(filterParams);
        return this.http.get<{ inventory: Inventory[]; embedded: Embedded; totalSize: number }>(INVENTORY_SERVICE_API_ROOT, { params }).pipe(
            map(({ inventory, embedded, totalSize }) => ({
                embedInventories: inventory.map((inventoryItem) => Inventory.toInventoryEmbedded(new Inventory(inventoryItem), embedded, embedFields)),
                totalSize,
            })
            ),
            catchError(this.errorService.errorHandler<EmbedInventories<T>>(this.errors, 'Récupération des données d\'inventaire', { embedInventories: [], totalSize: 0 }))
        );
    }

    public findLinkedInventories(inventoryId: string, filterParams: FilterParams): Observable<LinkedInventories> {
        const params = toParams(filterParams);
        return this.http.get<LinkedInventories>(`${INVENTORY_SERVICE_API_ROOT}/${inventoryId}/linked`, { params }).pipe(
            map(({ inventory, totalSize }) => ({
                inventory,
                totalSize
            })
            ),
            catchError(this.errorService.errorHandler<LinkedInventories>(this.errors, 'Récupération des données d\'inventaire', { inventory: [], totalSize: 0 }))
        );
    }

    public getInventory<T extends inventoryEmbed>(
        inventoryId: string,
        { embedFields = [], includeRemoved = false }: { embedFields?: T[]; includeRemoved?: boolean } = {}
    ): Observable<EmbedInventory<'$createdBy' | '$updatedBy' | '$deletedBy' | '$projects' | '$cell' | '$company'>> {
        const embeds = _.uniq(embedFields.map((it) => inventoryConfiguration[it].embed)).join(',');
        const params = toParams({ embed: embeds });
        return this.http.get<Inventory & { embedded: Embedded }>(
            INVENTORY_SERVICE_API_ROOT + '/' + inventoryId,
            { params: params.set('showDeleted', `${includeRemoved}`) }
        ).pipe(
            map(({ embedded, ...inventory }) => Inventory.toInventoryEmbedded(inventory, embedded, embedFields)),
            catchError(this.errorService.errorHandler<Inventory>(this.errors, 'Récupération de l\'inventaire', null))
        );
    }

    public getInventorySoftwares(inventoryId: string): Observable<Software[]> {
        return this.http.get<{ softwares: Software[] }>(`${INVENTORY_SERVICE_API_ROOT}/${inventoryId}/softwares`).pipe(
            map((result) => result.softwares),
            catchError(this.errorService.errorHandler<Software[]>(this.errors, 'Récupération des softwares d\'un inventaire', null))
        );
    }

    public getInventoryToolsAndServices(inventoryId: string): Observable<ToolAndService[]> {
        return this.http.get<{ toolsAndServices: ToolAndService[] }>(`${INVENTORY_SERVICE_API_ROOT}/${inventoryId}/tools-and-services`).pipe(
            map((result) => result.toolsAndServices),
            catchError(this.errorService.errorHandler<ToolAndService[]>(this.errors, 'Récupération des outils et services d\'un inventaire', []))
        );
    }

    public updateInventoryMetadata(inventoryId: string, metadata: Map<string, string>): Observable<Inventory> {
        const parsedMetadata = {};
        metadata.forEach((value: string, key: string) => {
            parsedMetadata[key] = value.trim();
        });

        return this.http.post<Inventory>(INVENTORY_SERVICE_API_ROOT + `/${inventoryId}:setMetadata`, parsedMetadata)
            .pipe(
                map(_inventory => new Inventory(_inventory)),
                catchError(this.errorService.errorHandler<Inventory>(this.errors, 'Modification des Metadata d\'un projet', null))
            );
    }

    public updateInventory(inventory: Partial<Inventory>, error = false): Observable<Inventory> {
        inventory.inventoryUpdatedSource = 'synapps';
        return this.http.patch<Inventory>(INVENTORY_SERVICE_API_ROOT + `/${inventory.id}`, inventory)
            .pipe(
                map(_inventory => new Inventory(_inventory)),
                catchError(this.errorService.errorHandler<Inventory>(this.errors, 'Modification de l\'inventaire', null, true))
                // catchError(error ?  (e) => throwError(e) : this.errorService.errorHandler<Inventory>(this.errors, 'Modification de l\'inventaire', null))
            );
    }
    // catchError(this.errorService.errorHandler<Inventory>(this.errors, 'Modification de l\'inventaire', null))
    public fetchTags(filterParams: FilterParams): Observable<Tag[]> {
        const params = toParams(filterParams);
        return this.http.get<{ tags: Tag[] }>(INVENTORY_SERVICE_API_ROOT + '/tags', { params }).pipe(
            map((result) => result.tags),
            catchError(this.errorService.errorHandler<Tag[]>(this.errors, 'Récupération des données de tag', []))
        );
    }

    public fetchMetadataKeys(filterParams: FilterParams): Observable<ListMetadataKeys> {
        return this.metadataService.fetchMetadataKeys(INVENTORY_SERVICE_API_ROOT, filterParams, this.errors);
    }

    public getSchema(inventory: { type: string; subtype: string }): Observable<InventorySchema> {
        const params = toParams({ filter: `type:${inventory.type} subtype:${inventory.subtype}` });

        return this.http.get<{ schemas: Array<InventorySchema & { schema: string }> }>(
            `${INVENTORY_SERVICE_API_ROOT}/schemas`, { params })
            .pipe(
                map(res => {
                    if (res.schemas.length === 0) {
                        throw new Error(`Schema not found for type:${inventory.type}/${inventory.subtype}`);
                    }

                    return res.schemas.pop();
                }),
                map((schema) => ({ ...schema, schema: JSON.parse(schema.schema) })),
                catchError(this.errorService.errorHandler<InventorySchema>(this.errors, 'Récupération du schema de l\'inventaire', new InventorySchema()))
            );
    }

    public exportInventories(filterParams: FilterParams): Observable<string | undefined> {
        const params = toParams({ ...filterParams, embed: 'projects' });
        return this.http.get<{ content: string }>(INVENTORY_SERVICE_API_ROOT + ':export', { params }).pipe(
            map((result) => result?.content),
            catchError(this.errorService.errorHandler<undefined>(this.errors, 'Export des données d\'inventaire', undefined))
        );
    }

    public deleteInventory(inventoryId: string): Observable<string> {
        return this.http.delete<string>(`${INVENTORY_SERVICE_API_ROOT}/${inventoryId}`)
            .pipe(
                catchError(this.errorService.errorHandler<string>(this.errors, 'Suppression d\'un inventaire', null))
            );
    }

    public getInventoryPatches<T extends patchEmbed>(inventoryId: string, embedFields: T[] = [], filterParams: FilterParams): Observable<EmbedPatches<T>> {
        const embeds = uniq(embedFields.map((it) => patchConfiguration[it].embed));
        const params = toParams({ ...filterParams, embed: !isEmpty(embeds) && embeds.join(',') });
        return this.http.get<{ patches: Patch[]; embedded: Embedded; totalSize: number }>(`${INVENTORY_SERVICE_API_ROOT}/${inventoryId}/patches`, { params }).pipe(
            map(({ patches, embedded, totalSize }) => ({
                patchHistory: Patch.toPatchesEmbedded(patches, embedded, embedFields),
                totalSize
            })),
            catchError(this.errorService.errorHandler<EmbedPatches<T>>(this.errors, 'Récupération de l\'historique des patchs', { patchHistory: [], totalSize: 0 }))
        );
    }

    public updatePatch(inventoryId: string, patchId: string, payload: Partial<Patch>): Observable<Patch | null> {
        return this.http.patch<Patch>(`${INVENTORY_SERVICE_API_ROOT}/${inventoryId}/patches/${patchId}`, payload).pipe(
            map((patch) => Patch.toPatchEmbedded(patch)),
            catchError(this.errorService.errorHandler<Patch | null>(this.errors, 'Mise à jour \'un patch', null))
        );
    }

    public getInventoryPatchSoftWares(inventoryId: string, patchId: string): Observable<Software[]> {
        return this.http.get<{ softwares: Software[] }>(
            `${INVENTORY_SERVICE_API_ROOT}/${inventoryId}/patches/${patchId}/softwares`
        ).pipe(
            map((result) => result.softwares),
            catchError(this.errorService.errorHandler<Software[]>(this.errors, 'Récupération des softwares d\'un patch', null))
        );
    }

    public computeInventoriesStats(filterParams: FilterParams & { aggregate?: string; groupBy?: string }): Observable<Stat[]> {
        const params = toParams(filterParams);
        return this.http.get<{ stats: Stat[] }>(`${INVENTORY_SERVICE_API_ROOT}/stats`, { params }).pipe(
            map((result) => result.stats),
            catchError(this.errorService.errorHandler<Stat[]>(this.errors, 'Récupération des stats des inventaires', null))
        );
    }

    public bulkEditInventory(inventoryIds: string[], operations: Operation[]): Observable<BulkEditInventoryResponse> {
        return this.http.post<BulkEditInventoryResponse>(`${INVENTORY_SERVICE_API_ROOT}/bulk-edit`, {
            inventoryIds,
            operations
        }).pipe(
            catchError(
                this.errorService.errorHandler<BulkEditInventoryResponse>(this.errors, 'Modification des inventaires en masse', null))
        );
    }

    public listInventoryCves(inventoryId: string): Observable<CveRaw[]>{
        return this.http.get<{cves: CveRaw[]}>(`${INVENTORY_SERVICE_API_ROOT}/${inventoryId}/cves`).pipe(
            map(result => result.cves),
            catchError(
                this.errorService.errorHandler<CveRaw[]>(this.errors, 'Récupération des cves d\'un inventaire', null))
        );
    }
}
